
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

export function AIChatbot() {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatWindowRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Welcome message
      const welcomeMessage: Message = {
        id: Date.now().toString(),
        text: t('chatbot.welcomeMessage'),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, t, messages.length]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Listen for custom event to open chatbot
  useEffect(() => {
    const handleOpenChatbot = () => {
      setIsOpen(true);
    };
    
    window.addEventListener('openAIChatbot', handleOpenChatbot);
    return () => {
      window.removeEventListener('openAIChatbot', handleOpenChatbot);
    };
  }, []);

  // Handle escape key to close chat
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Trap focus within chat window when open
  useEffect(() => {
    if (isOpen && chatWindowRef.current) {
      const focusableElements = chatWindowRef.current.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const firstElement = focusableElements[0] as HTMLElement;
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement?.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement?.focus();
          }
        }
      };

      document.addEventListener('keydown', handleTabKey);
      return () => document.removeEventListener('keydown', handleTabKey);
    }
  }, [isOpen]);

  const getAIResponse = (userMessage: string): string => {
    const lowerMessage = userMessage.toLowerCase();

    // Pricing questions
    if (lowerMessage.includes('pris') || lowerMessage.includes('price') || lowerMessage.includes('kost') || lowerMessage.includes('cost')) {
      return t('chatbot.answerPricing');
    }

    // Services questions
    if (lowerMessage.includes('tjeneste') || lowerMessage.includes('service') || lowerMessage.includes('tilbud') || lowerMessage.includes('offer')) {
      return t('chatbot.answerServices');
    }

    // Timeline questions
    if (lowerMessage.includes('tid') || lowerMessage.includes('time') || lowerMessage.includes('hvor lang') || lowerMessage.includes('how long') || lowerMessage.includes('når') || lowerMessage.includes('when')) {
      return t('chatbot.answerTimeline');
    }

    // Process questions
    if (lowerMessage.includes('prosess') || lowerMessage.includes('process') || lowerMessage.includes('hvordan') || lowerMessage.includes('how')) {
      return t('chatbot.answerProcess');
    }

    // Contact questions
    if (lowerMessage.includes('kontakt') || lowerMessage.includes('contact') || lowerMessage.includes('epost') || lowerMessage.includes('email') || lowerMessage.includes('telefon') || lowerMessage.includes('phone')) {
      return t('chatbot.answerContact');
    }

    // SEO questions
    if (lowerMessage.includes('seo') || lowerMessage.includes('google') || lowerMessage.includes('søk') || lowerMessage.includes('search')) {
      return t('chatbot.answerSEO');
    }

    // Support questions
    if (lowerMessage.includes('support') || lowerMessage.includes('hjelp') || lowerMessage.includes('help') || lowerMessage.includes('vedlikehold') || lowerMessage.includes('maintenance')) {
      return t('chatbot.answerSupport');
    }

    // Booking questions
    if (lowerMessage.includes('book') || lowerMessage.includes('møte') || lowerMessage.includes('meeting') || lowerMessage.includes('konsultasjon') || lowerMessage.includes('consultation')) {
      return t('chatbot.answerBooking');
    }

    // Default response
    return t('chatbot.answerDefault');
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI thinking time
    setTimeout(() => {
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: getAIResponse(inputValue),
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 800);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center cursor-pointer focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2 ${
          isOpen ? 'scale-0' : 'scale-100'
        }`}
        aria-label={t('chatbot.openChat')}
        aria-expanded={isOpen}
        aria-controls="chat-window"
      >
        <i className="ri-robot-line text-2xl" aria-hidden="true"></i>
      </button>

      {/* Chat Window */}
      <div
        ref={chatWindowRef}
        id="chat-window"
        role="dialog"
        aria-modal="true"
        aria-label={t('chatbot.title')}
        className={`fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)] bg-white rounded-2xl shadow-2xl transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        }`}
        style={{ height: '600px', maxHeight: 'calc(100vh - 3rem)' }}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white p-4 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center" aria-hidden="true">
              <i className="ri-robot-line text-xl"></i>
            </div>
            <div>
              <h2 className="font-semibold text-sm">{t('chatbot.title')}</h2>
              <p className="text-xs text-violet-100">{t('chatbot.subtitle')}</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-lg transition-colors cursor-pointer focus:ring-2 focus:ring-white/50"
            aria-label={t('chatbot.closeChat')}
          >
            <i className="ri-close-line text-xl" aria-hidden="true"></i>
          </button>
        </div>

        {/* Messages */}
        <div 
          className="flex-1 overflow-y-auto p-4 space-y-4" 
          style={{ height: 'calc(100% - 140px)' }}
          role="log"
          aria-live="polite"
          aria-label="Chat meldinger"
        >
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                  message.sender === 'user'
                    ? 'bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white'
                    : 'bg-slate-100 text-slate-800'
                }`}
                role="article"
                aria-label={message.sender === 'user' ? 'Din melding' : 'AI svar'}
              >
                <p className="text-sm leading-relaxed whitespace-pre-line">{message.text}</p>
              </div>
            </div>
          ))}
          {isTyping && (
            <div className="flex justify-start" aria-live="polite">
              <div className="bg-slate-100 rounded-2xl px-4 py-3" role="status" aria-label="AI skriver...">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                  <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                </div>
                <span className="sr-only">AI skriver et svar...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-slate-200">
          <form 
            onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
            className="flex gap-2"
          >
            <label htmlFor="chat-input" className="sr-only">Skriv din melding</label>
            <input
              ref={inputRef}
              id="chat-input"
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('chatbot.inputPlaceholder')}
              className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-fuchsia-500 text-sm"
              autoComplete="off"
            />
            <button
              type="submit"
              disabled={!inputValue.trim()}
              className="w-10 h-10 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white rounded-lg hover:from-violet-600 hover:to-fuchsia-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center cursor-pointer focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2"
              aria-label={t('chatbot.sendMessage')}
            >
              <i className="ri-send-plane-fill text-lg" aria-hidden="true"></i>
            </button>
          </form>
        </div>
      </div>
    </>
  );
}
